import { useId, useState } from 'react';
import { useI18n } from '../../i18n/I18nContext.js';

export interface StockInputProps {
  label: string;
  /** Current committed value. `null` while the field is empty. */
  valueQty: number | null;
  onChange(qty: number | null, valid: boolean): void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Same override as `MoneyInput`'s — a real accessible name for a compact, visually unlabeled cell (table-edit mode). */
  ariaLabel?: string;
  /** Same table-cell variant as `MoneyInput`'s: one line, validation message moved to `title` + `aria-describedby` so it cannot reflow the row. */
  compact?: boolean;
}

const STOCK_RE = /^-?\d+(\.\d{1,3})?$/;

/**
 * Plain decimal quantity entry — up to three decimals, positive or
 * negative (an oversold product's stock is legitimately negative, see
 * db/migrations/005_stock_media.sql). Right-aligned, tabular figures, same
 * house rule as every other numeric field in this app.
 */
export function StockInput({
  label,
  valueQty,
  onChange,
  disabled,
  autoFocus,
  ariaLabel,
  compact,
}: StockInputProps) {
  const { t } = useI18n();
  const id = useId();
  const describedById = `${id}-desc`;
  const [raw, setRaw] = useState(() => (valueQty == null ? '' : String(valueQty)));
  const [touched, setTouched] = useState(false);

  const trimmed = raw.trim();
  const parsed = trimmed === '' ? null : STOCK_RE.test(trimmed) ? Number(trimmed) : null;
  const invalid = touched && trimmed !== '' && parsed === null;

  function handleChange(next: string) {
    setRaw(next);
    setTouched(true);
    const nextTrimmed = next.trim();
    const value = nextTrimmed === '' ? null : STOCK_RE.test(nextTrimmed) ? Number(nextTrimmed) : null;
    onChange(value, nextTrimmed === '' || value !== null);
  }

  const field = (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      className="numeric"
      style={{ textAlign: 'right', width: '100%' }}
      placeholder="0"
      value={raw}
      disabled={disabled}
      autoFocus={autoFocus}
      aria-invalid={invalid}
      aria-label={compact ? (ariaLabel ?? label) : ariaLabel}
      aria-describedby={compact && invalid ? describedById : undefined}
      title={compact && invalid ? t('products.stockInvalid') : undefined}
      onChange={(e) => handleChange(e.target.value)}
    />
  );

  if (compact) {
    return (
      <div className="field field--compact">
        {field}
        {/* Out of flow (position: absolute), so it describes without occupying. */}
        <span id={describedById} className="sr-only">
          {invalid ? t('products.stockInvalid') : ''}
        </span>
      </div>
    );
  }

  return (
    <label htmlFor={id}>
      {label}
      {field}
      {invalid && <span className="field-error">{t('products.stockInvalid')}</span>}
    </label>
  );
}
